import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Send, Banknote, Wallet, Plus, X, AlertTriangle } from "lucide-react";
import { useDisbursementQueue, useDisbursementMeta } from "../../lib/adminQueues";
import { useSelection } from "../../lib/useSelection";
import { SelectCheckbox } from "./SelectCheckbox";
import { BulkActionBar } from "./BulkActionBar";
import { BulkConfirmDialog } from "./BulkConfirmDialog";
import { SummaryList } from "./CampaignReviewTab";
import { NewGrantDialog, PAYOUT_BADGE, PAYOUT_LABEL } from "./NewGrantDialog";
import { TopUpDialog, TopUpList } from "./TopUpDialog";
import { GrantHistory } from "./GrantHistory";
import { StatusBadge } from "./_ui";

const money = (cents) => `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const SKIP_COPY = {
  not_onboarded: "student hasn't finished Stripe onboarding",
  survey_pending: "final installment is held until the student's beta survey is in (waive under Feedback)",
  insufficient_funds: "MRA's available Stripe balance is too low",
  stripe_error: "Stripe rejected the transfer",
  not_scheduled: "no longer scheduled",
};

export const DisbursementTab = () => {
  const { items, loading, error, refresh, bulkAuthorize, createGrant, cancelGrant } = useDisbursementQueue();
  const meta = useDisbursementMeta();
  const sel = useSelection();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [topUpOpen, setTopUpOpen] = useState(false);

  const ids = useMemo(() => items.map((d) => d.id), [items]);
  const selectedItems = items.filter((d) => sel.isSelected(d.id));
  const selectedTotal = selectedItems.reduce((sum, d) => sum + d.amount_cents, 0);
  const pendingTotal = items.reduce((sum, d) => sum + d.amount_cents, 0);
  const available = meta.balance?.available_cents ?? null;
  const overBalance = available !== null && selectedTotal > available;
  const shortfall = available !== null ? Math.max((sel.count > 0 ? selectedTotal : pendingTotal) - available, 0) : 0;
  const notReady = selectedItems.filter((d) => !d.payouts_ready).length;

  const runTopUp = async (body) => {
    const row = await meta.topUp(body);
    toast.success(`Top-up of ${money(row.amount_cents)} requested.`, {
      description: row.expected_availability ? `Stripe expects it to land ${new Date(`${row.expected_availability}T12:00:00Z`).toLocaleDateString(undefined, { month: "long", day: "numeric" })}.` : "Stripe is pulling it from MRA's bank now.",
    });
    meta.refresh();
  };

  const runAuthorize = async () => {
    const res = await bulkAuthorize(sel.ids);
    const p = res.processed || 0;
    const reasons = Object.entries(res.skipped_reasons || {}).map(([k, n]) => `${n} skipped — ${SKIP_COPY[k] || k}`).join("; ");
    if (p > 0) {
      toast.success(`${p} grant${p === 1 ? "" : "s"} sent — ${money(res.total_cents || 0)}.`, { description: reasons || "Transfers created in Stripe. Students were emailed." });
    } else {
      toast.error("No grants were sent.", { description: reasons || "Nothing eligible in the selection." });
    }
    sel.clear();
    setConfirmOpen(false);
    meta.refresh();
  };

  const runCreate = async (body) => {
    const row = await createGrant(body);
    toast.success(`Scheduled ${money(row.amount_cents)} for ${row.student_name}`, { description: `Week ending ${row.week_ending}.` });
    meta.refresh();
  };

  const runCancel = async (d) => {
    await cancelGrant(d.id);
    toast(`Cancelled ${money(d.amount_cents)} for ${d.student_name}`);
    meta.refresh();
  };

  return (
    <div data-testid="tab-disbursements">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <p data-testid="disbursements-live-banner" className="inline-flex items-center gap-2 rounded-full bg-grass/15 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-ink">
          <span className="h-1.5 w-1.5 rounded-full bg-grass" /> Stripe Connect · {meta.balance ? (meta.balance.livemode ? "live" : "sandbox") : "…"}
        </p>
        <button type="button" onClick={() => setNewOpen(true)} data-testid="disb-new-grant" className="inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 text-xs font-semibold text-sand transition-colors hover:bg-forest">
          <Plus className="h-3.5 w-3.5" strokeWidth={2.5} /> New grant
        </button>
      </div>

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatChip
          label="MRA available balance"
          value={available !== null ? money(available) : "—"}
          hint={meta.balance ? `${money(meta.balance.pending_cents)} pending${meta.balance.incoming_topups_cents ? ` · +${money(meta.balance.incoming_topups_cents)} in transit` : ""}` : "Stripe unavailable"}
          testid="disb-stat-balance"
          action={<button type="button" onClick={() => setTopUpOpen(true)} data-testid="disb-topup-btn" className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-ink/20 bg-bone px-3 py-1 text-[11px] font-semibold text-ink transition-colors hover:border-ink hover:bg-ink hover:text-sand"><Plus className="h-3 w-3" strokeWidth={2.5} /> Add funds</button>}
        />
        <StatChip label="Scheduled" value={money(pendingTotal)} hint={`${items.length} grant${items.length === 1 ? "" : "s"}`} testid="disb-stat-pending" />
        <StatChip label="Selected" value={money(selectedTotal)} hint={`${sel.count} grant${sel.count === 1 ? "" : "s"}`} highlight={sel.count > 0} testid="disb-stat-selected" />
        <StatChip label="This week ends" value={items[0]?.week_ending || "—"} hint="Weekly batch · Friday" testid="disb-stat-week" />
      </div>

      {shortfall > 0 && (
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border-2 border-tangerine/40 bg-tangerine/[0.06] px-5 py-3" data-testid="disb-shortfall-banner">
          <p className="flex items-start gap-2 text-sm text-ink">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-tangerine" strokeWidth={2} />
            <span>{sel.count > 0 ? "The selected grants" : "This week's grants"} exceed MRA's available balance by <strong>{money(shortfall)}</strong>. Transfers beyond the balance will be skipped.</span>
          </p>
          <button type="button" onClick={() => setTopUpOpen(true)} data-testid="disb-shortfall-topup" className="rounded-full bg-ink px-4 py-2 text-xs font-semibold text-sand transition-colors hover:bg-forest">Add {money(shortfall)}</button>
        </div>
      )}

      <TopUpList items={meta.topups} />

      {error && <p className="mb-6 rounded-md bg-scarlet/8 px-3 py-2 text-sm text-scarlet">{error}</p>}

      {items.length > 0 && (
        <div className="mb-4 flex items-center justify-between rounded-lg border border-border bg-bone px-5 py-3">
          <label className="flex items-center gap-3 text-sm font-medium text-ink">
            <SelectCheckbox testid="disb-select-all" checked={sel.allSelected(ids)} indeterminate={sel.count > 0 && !sel.allSelected(ids)} onChange={(v) => (v ? sel.selectAll(ids) : sel.clear())} label="Select all grants" />
            {sel.count === 0 ? `Select all (${items.length})` : `${sel.count} of ${items.length} selected`}
          </label>
          <button type="button" onClick={() => { refresh(); meta.refresh(); }} data-testid="disb-refresh" className="text-xs font-semibold uppercase tracking-[0.18em] text-olive hover:text-ink">Refresh</button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-olive"><Loader2 className="h-5 w-5 animate-spin" /> Loading grants…</div>
      ) : items.length === 0 ? (
        <div data-testid="disb-empty" className="rounded-2xl border-2 border-dashed border-border bg-bone p-10 text-center">
          <Banknote className="mx-auto h-9 w-9 text-olive" strokeWidth={1.5} />
          <h3 className="mt-3 font-serif text-2xl text-ink">No grants scheduled.</h3>
          <p className="mt-1 text-sm text-olive">Schedule a grant against an approved campaign to start this week's batch.</p>
        </div>
      ) : (
        <div className="space-y-3">
          <AnimatePresence>
            {items.map((d) => <DisbRow key={d.id} d={d} checked={sel.isSelected(d.id)} onToggle={() => sel.toggle(d.id)} onCancel={() => runCancel(d)} />)}
          </AnimatePresence>
        </div>
      )}

      <GrantHistory items={meta.history} livemode={meta.balance?.livemode} />

      <BulkActionBar count={sel.count} label="grant" onClear={sel.clear}>
        <span className="rounded-full bg-sand/5 px-3 py-1.5 text-xs font-semibold text-sand" data-testid="disb-bulk-total">Total: {money(selectedTotal)}</span>
        <button type="button" onClick={() => setConfirmOpen(true)} data-testid="disb-bulk-authorize" className="inline-flex items-center gap-1.5 rounded-full bg-yellow px-4 py-2 text-xs font-semibold text-ink transition-colors hover:brightness-95">
          <Send className="h-3.5 w-3.5" strokeWidth={2} /> Authorize batch
        </button>
      </BulkActionBar>

      <BulkConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={<>Send {sel.count} grant{sel.count === 1 ? "" : "s"} · {money(selectedTotal)}?</>}
        description={
          <>
            Each grant becomes a Stripe transfer from My Rich Aunt's balance to the student's connected account, and the student is emailed. This can't be undone from here.
            {overBalance && <span className="mt-2 block font-semibold text-scarlet">Selected total exceeds MRA's available balance ({money(available)}). Some transfers will be skipped.</span>}
            {notReady > 0 && <span className="mt-2 block font-semibold text-tangerine">{notReady} selected student{notReady === 1 ? " hasn't" : "s haven't"} finished Stripe onboarding and will be skipped.</span>}
          </>
        }
        itemsSummary={<SummaryList items={selectedItems.map((d) => ({ left: d.student_name, right: money(d.amount_cents) }))} />}
        confirmLabel="Send grants"
        onConfirm={runAuthorize}
      />

      <NewGrantDialog open={newOpen} onOpenChange={setNewOpen} options={meta.options} onCreate={runCreate} />
      <TopUpDialog open={topUpOpen} onOpenChange={setTopUpOpen} suggestedCents={shortfall} onSubmit={runTopUp} />
    </div>
  );
};

const StatChip = ({ label, value, hint, highlight = false, testid, action = null }) => (
  <div data-testid={testid} className={`rounded-2xl border-2 p-5 transition-colors ${highlight ? "border-ink bg-yellow/10" : "border-ink/10 bg-bone"}`}>
    <div className="flex items-start justify-between gap-2">
      <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-olive leading-snug">{label}</p>
      {action}
    </div>
    <p className="mt-1 font-serif text-3xl text-ink">{value}</p>
    {hint && <p className="mt-1 text-xs text-olive">{hint}</p>}
  </div>
);

const DisbRow = ({ d, checked, onToggle, onCancel }) => (
  <motion.article
    layout
    initial={{ opacity: 0, y: 6 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -6, height: 0 }}
    transition={{ duration: 0.25, ease: [0.22, 0.61, 0.36, 1] }}
    className={`flex items-start gap-4 rounded-xl border-2 p-5 transition-colors ${checked ? "border-ink bg-yellow/5" : "border-ink/10 bg-bone hover:border-ink/25"}`}
    data-testid={`disb-row-${d.id}`}
  >
    <div className="pt-0.5">
      <SelectCheckbox checked={checked} onChange={onToggle} testid={`disb-checkbox-${d.id}`} label={`Select ${d.student_name}'s grant`} />
    </div>
    <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${d.payouts_ready ? "bg-grass/12 text-grass" : "bg-tangerine/12 text-tangerine"}`}>
      <Wallet className="h-5 w-5" strokeWidth={1.75} />
    </div>
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="truncate font-serif text-lg text-ink" data-testid={`disb-name-${d.id}`}>{d.student_name}</p>
        <p className="shrink-0 font-serif text-2xl text-ink" data-testid={`disb-amount-${d.id}`}>{money(d.amount_cents)}</p>
      </div>
      <p className="mt-0.5 text-sm text-olive">{d.school} · <span className="italic">{d.campaign_title}</span></p>
      {d.memo && <p className="mt-1 text-xs text-ink/70">“{d.memo}”</p>}
      <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-olive">
        <StatusBadge status={PAYOUT_BADGE[d.payout_state] || "pending"} />
        <span data-testid={`disb-payout-state-${d.id}`}>{PAYOUT_LABEL[d.payout_state]}</span>
        <span>· {d.student_email}</span>
      </div>
      {d.blocked_reason && (
        <p className="mt-2 flex items-start gap-1.5 text-xs text-scarlet" data-testid={`disb-blocked-${d.id}`}>
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={2} />
          Last attempt skipped: {SKIP_COPY[d.blocked_reason] || d.blocked_reason}{d.last_error ? ` (${d.last_error})` : ""}.
        </p>
      )}
    </div>
    <button type="button" onClick={onCancel} aria-label={`Cancel ${d.student_name}'s grant`} data-testid={`disb-cancel-${d.id}`} className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-olive transition-colors hover:bg-scarlet/10 hover:text-scarlet">
      <X className="h-4 w-4" strokeWidth={2} />
    </button>
  </motion.article>
);
